import { API } from '@/src/api/api'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queryKeys } from '../api/queryKeys'
import { env } from '../env'
import { mapInvitationToInvitedUser } from '../features/user-management/types/invitedUser.interface'
import type { InvitedUser } from '../features/user-management/types/invitedUser.interface'
import type { User } from '../types/user'
import { ECOPLANET_DOMAIN, ONE_MINUTE_IN_MS } from '../utils/constants'
import { useSessionStore } from './use_session_store'

interface UseUsersOptions {
  fetchAllCompanyUsers?: boolean
  enabled?: boolean
}

export const useUsers = ({ fetchAllCompanyUsers = false, enabled = true }: UseUsersOptions = {}) => {
  const session = useSessionStore()
  const companyFirebaseId = session.user?.companyId
  const companyPgId = session.user?.company.id
  const registeredUsersQueryEnabled = enabled && (fetchAllCompanyUsers ? !!companyPgId : !!companyFirebaseId)

  const query = useQuery<User[], any, (User | InvitedUser)[]>({
    queryKey: queryKeys.users.manyByCompanyId.toKeyWithArgs(companyFirebaseId, companyPgId, fetchAllCompanyUsers),
    enabled: registeredUsersQueryEnabled,
    staleTime: ONE_MINUTE_IN_MS * 5,
    queryFn: async () => {
      const users = fetchAllCompanyUsers
        ? await API.users.fetch({ companyId: companyPgId! })
        : await API.users.fetch({ locationFirebaseId: companyFirebaseId! })
      return users
    },
    select: users => {
      if (!fetchAllCompanyUsers) {
        return env.isDev ? users : users.filter(user => !user.email.endsWith(ECOPLANET_DOMAIN))
      }
      const filteredUsers = users.filter(user => !user.email.toLowerCase().endsWith(ECOPLANET_DOMAIN))
      return filteredUsers
    },
  })

  const invitationsQuery = useQuery({
    queryKey: queryKeys.invitations.manyByCompanyId.toKeyWithArgs(companyPgId),
    enabled: enabled && !!companyPgId,
    queryFn: () => API.invitations.fetchMany(companyPgId!),
  })

  const data = useMemo(() => {
    const registeredUsers = query.data ?? []
    const invitedUsers = (invitationsQuery.data?.map(mapInvitationToInvitedUser) ?? []).filter(
      user => !fetchAllCompanyUsers || !user.email.toLowerCase().endsWith(ECOPLANET_DOMAIN)
    )
    return [...registeredUsers, ...invitedUsers]
  }, [fetchAllCompanyUsers, query.data, invitationsQuery.data])

  const isLoading = query.isLoading || invitationsQuery.isLoading

  return {
    ...query,
    data,
    isLoading,
    isUsersLoading: query.isLoading,
    isInvitationsLoading: invitationsQuery.isLoading,
  }
}

export const useUserById = (id?: number) => {
  return useQuery({
    enabled: !!id,
    queryKey: queryKeys.users.byId.toKeyWithArgs(id),
    queryFn: async () => {
      if (!id) return undefined
      const users = await API.users.getById({ id })
      return users
    },
  })
}
